import type en from "../en/library";
import type { Translation } from "../../types";

const library: Translation<typeof en> = {
  title: "Bibliothèque",
  intro:
    "Toutes les vidéos de l’espace de travail — réunions, fichiers importés et montages publiés. Faites-les glisser sur un dossier pour les ranger ; ouvrez Studio pour couper et assembler.",
  sort: {
    label: "Trier",
    newest: "Plus récentes d’abord",
    oldest: "Plus anciennes d’abord",
    longest: "Plus longues d’abord",
    largest: "Plus volumineuses d’abord",
    name: "Par titre",
  },
  folders: {
    heading: "Dossiers",
    new: "Nouveau dossier",
    namePlaceholder: "Nom du dossier",
    home: "Accueil",
    path: "Chemin du dossier",
    moveRejected: "Ce déplacement a été refusé.",
    expand: "Déplier",
    collapse: "Replier",
    renameNamed: "Renommer {{name}}",
    deleteNamed: "Supprimer {{name}}",
    confirmDelete:
      "Supprimer « {{name}} » ? Les enregistrements et dossiers qu’il contient remontent au premier niveau — rien n’est supprimé avec lui.",
    openOrDrop: "Ouvrir {{name}} — ou déposez une vidéo ici pour l’y ranger",
    moveTo: "Déplacer vers un dossier",
  },
  toolbar: {
    search: "Rechercher…",
    searchAll: "Rechercher dans toutes les vidéos…",
    searchLibrary: "Rechercher dans la bibliothèque…",
    gridView: "Vue en grille",
    listView: "Vue en liste",
    flatHint: "Afficher toutes les vidéos dans une seule liste, sans tenir compte des dossiers",
    allVideos: "Toutes les vidéos",
    recordMeeting: "Enregistrer une réunion",
    clearSearch: "Effacer la recherche",
  },
  count: {
    videos_one: "{{count}} vidéo",
    videos_other: "{{count}} vidéos",
    folders_one: "{{count}} dossier",
    folders_other: "{{count}} dossiers",
    searching_one: "Recherche dans tous les dossiers — {{count}} vidéo correspondante",
    searching_other: "Recherche dans tous les dossiers — {{count}} vidéos correspondantes",
    workspace_one: "{{count}} vidéo dans tout l’espace de travail",
    workspace_other: "{{count}} vidéos dans tout l’espace de travail",
  },
  pagination: {
    previous: "Précédente",
    next: "Suivante",
    pageOf: "Page {{page}} sur {{total}}",
  },
  empty: {
    loadRecordingsFailed: "Impossible de charger vos enregistrements",
    loadLibraryFailed: "Impossible de charger la bibliothèque",
    reloadHint: "Rechargez la page, et vérifiez votre connexion si cela se reproduit.",
    nothingMatches: "Aucun résultat",
    tryDifferent: "Essayez d’autres mots, ou effacez la recherche.",
    folderEmpty: "Ce dossier est vide",
    folderEmptyHint: "Faites glisser un enregistrement ici, ou ajoutez-en un directement dans ce dossier.",
    noRecordings: "Aucun enregistrement pour l’instant",
    noRecordingsHint:
      "Envoyez un assistant de prise de notes dans un appel Zoom ou Google Meet, importez un fichier que vous avez déjà, ou importez une vidéo depuis YouTube ou LinkedIn.",
    noVideos: "Aucune vidéo pour l’instant",
    noVideosHint: "Importez une vidéo ou enregistrez une réunion — tout arrive ici.",
    clearOrPick: "Effacez la recherche ou choisissez un autre dossier.",
  },
  card: {
    stop: "Arrêter",
    moveNamed: "Déplacer {{title}}",
    dragHint: "Faites glisser vers un dossier, ou appuyez pour en choisir un",
    deleteNamed: "Supprimer {{title}}",
    deleteRecording: "Supprimer l’enregistrement",
    confirmDeleteShort:
      "Supprimer <strong>« {{title}} »</strong> ? La vidéo, la transcription, les chapitres et les discussions disparaissent avec. Définitif.",
    confirmDeleteTitle: "Supprimer « {{title}} » ?",
    confirmDeleteBody:
      "La vidéo, sa transcription, ses chapitres et toutes les discussions à son sujet sont supprimés définitivement. Cette action est irréversible.",
    deletePermanently: "Supprimer définitivement",
    inFolder: "Dans {{name}}",
  },
  menu: {
    actions: "Actions",
    editInStudio: "Monter dans Studio",
    availableWhenReady: "Disponible dès que la vidéo est prête.",
    moveToFolder: "Déplacer vers un dossier…",
    rename: "Renommer…",
    delete: "Supprimer…",
    moveTo: "Déplacer vers",
    noFolder: "Aucun dossier",
    confirmDelete:
      "Supprimer cette vidéo, sa transcription, ses chapitres et ses discussions ? Cette action est irréversible.",
    deleted: "Supprimé.",
  },
  deleteImpact: {
    drafts_one: "Utilisée dans {{count}} brouillon Studio — ces extraits cesseront de fonctionner.",
    drafts_other:
      "Utilisée dans {{count}} brouillons Studio — ces extraits cesseront de fonctionner.",
    edits_one:
      "{{count}} montage publié à partir de cette vidéo reste lisible (il possède sa propre copie), mais perd son lien avec cette source.",
    edits_other:
      "{{count}} montages publiés à partir de cette vidéo restent lisibles (ils possèdent leur propre copie), mais perdent leur lien avec cette source.",
  },
  picker: {
    title: "Ajouter depuis la bibliothèque",
    hint: "Chaque choix s’ajoute à la fin de la ligne de temps — ajoutez-en plusieurs, puis fermez et organisez-les.",
    folderFilter: "N’afficher qu’un dossier",
    allFolders: "Tous les dossiers",
    notInFolder: "Hors dossier",
    loadFailedHint: "Fermez cette fenêtre et réessayez dans un instant.",
    emptyHint: "Importez un fichier depuis le panneau Sources, ou enregistrez d’abord une réunion.",
    alreadyAdded: "Déjà sur la ligne de temps — ajoutez-la à nouveau pour la dupliquer",
    addToEnd: "Ajouter à la fin de la ligne de temps",
    added: "Ajoutée",
  },
  upload: {
    limits: "MP4, MOV, WebM ou audio · jusqu’à {{size}} · jusqu’à 10 heures",
    resuming: "Reprise {{percent}} %",
    uploading: "Import {{percent}} %",
    tooLarge:
      "Ce fichier fait {{size}}. La limite est de {{limit}}, car les enregistrements plus volumineux ne peuvent pas être transcrits.",
    tooLong:
      "Les enregistrements doivent durer moins de 10 heures — au-delà, ils ne peuvent pas être transcrits.",
    failedResume: "{{message}} Choisissez à nouveau le même fichier pour reprendre.",
    failed: "L’import a échoué. Choisissez à nouveau le même fichier pour reprendre.",
    errors: {
      partRejected: "Partie refusée ({{status}})",
      noEtag:
        "Le bucket de stockage n’expose pas l’en-tête ETag : l’import ne peut donc pas être finalisé.",
      connectionDropped: "La connexion a été interrompue.",
      partNotSigned: "Une partie n’a pas été signée.",
    },
  },
  importLink: {
    title: "Importer depuis un lien",
    intro:
      "Collez un lien {{sources}}. Nous récupérons la vidéo, puis la transcrivons comme n’importe quel fichier importé.",
    videoLink: "Lien de la vidéo",
    recognised: "Lien {{source}} reconnu",
    unrecognised: "Collez le lien direct d’une vidéo ou d’une publication {{sources}}.",
    titleLabel: "Titre",
    titleHint: "(facultatif — sinon, le titre de la vidéo)",
    lookingOn: "Recherche de la vidéo sur {{source}}…",
    lookingOnPage: "Recherche de la vidéo sur la page…",
    rejected: "Ce lien n’a pas pu être importé.",
    startFailed: "L’import n’a pas pu démarrer.",
    pasteFirst: "Collez d’abord un lien",
    onlySources: "Seuls les liens {{sources}} peuvent être importés",
    checking: "Vérification du lien…",
    submit: "Importer la vidéo",
    legal:
      "Vidéos publiques et non répertoriées uniquement, jusqu’à {{size}} et 10 heures. N’importez que des vidéos que vous avez le droit d’utiliser.",
    found:
      "« {{title}} » trouvée. Le téléchargement est en cours ; elle apparaîtra dans votre bibliothèque une fois prête.",
    stillChecking:
      "Vérification du lien toujours en cours. Il figure dans votre bibliothèque, et sa carte indiquera tout problème.",
    buttonTitle: "Importer une vidéo depuis {{sources}}",
    buttonCompact: "Lien",
    button: "Importer un lien",
  },
  sendBot: {
    title: "Enregistrer une réunion",
    intro: "Un assistant de prise de notes rejoint l’appel et apparaît dans la liste des participants.",
    meetingLink: "Lien de la réunion",
    urlPlaceholder: "https://zoom.us/j/… ou https://meet.google.com/…",
    recognised: "Lien {{platform}} reconnu",
    unsupported: "Seuls les liens Zoom et Google Meet sont pris en charge.",
    titleLabel: "Titre",
    optional: "(facultatif)",
    titlePlaceholder: "Point hebdomadaire",
    when: "Quand",
    joinNow: "Rejoindre maintenant",
    joinNowHint: "L’appel a déjà commencé",
    schedule: "Planifier",
    scheduleHint: "Pour une réunion ultérieure",
    tooSoon:
      "Planifiez au moins 10 minutes à l’avance — sinon l’assistant risque de manquer le début.",
    joinsEarly: "L’assistant rejoint l’appel quelques minutes en avance et patiente.",
    failed: "Impossible d’envoyer l’assistant.",
    submitNow: "Envoyer l’assistant",
    submitScheduled: "Planifier",
    consent:
      "Tous les participants à l’appel voient l’assistant de prise de notes dans la liste des participants. Enregistrer à l’insu des gens n’est pas quelque chose que ce service fait.",
  },
};

export default library;
